/**
 * CareDetailsDialog — quick edit of the names that personalize the app
 * (caregiver + loved one + relationship), with jump-offs to the full
 * intake and patient profile editors. Used on Dashboard and Roadmap.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ClipboardList, HeartPulse } from "lucide-react";

const RELATIONSHIPS = ["Daughter", "Son", "Spouse / partner", "Grandchild", "Sibling", "Friend / neighbor", "Other"];

export function CareDetailsDialog({ open, onOpenChange, sid, profile, onSaved }) {
    const [cg, setCg] = useState("");
    const [lo, setLo] = useState("");
    const [rel, setRel] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setCg(profile?.caregiver_name || "");
            setLo(profile?.loved_one_name || "");
            setRel(profile?.relationship || "");
        }
    }, [open, profile]);

    const save = async () => {
        setSaving(true);
        const patch = { caregiver_name: cg.trim(), loved_one_name: lo.trim(), relationship: rel };
        try {
            const r = await api.put(`/profile/${sid}`, patch);
            onSaved?.(r.data && typeof r.data === "object" ? { ...r.data, ...patch } : patch);
            toast.success("Care details updated.");
            onOpenChange(false);
        } catch {
            toast.error("Couldn't save — please try again.");
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "w-full mt-1 px-3.5 py-2.5 rounded-xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md bg-[#FDFBF4]" data-testid="care-details-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading">Update care details</DialogTitle>
                    <DialogDescription>
                        These names personalize your dashboard, roadmap and Lane's guidance.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <label className="block text-xs font-semibold text-[#4C5551]">
                        Your name (the caregiver)
                        <input value={cg} onChange={e => setCg(e.target.value)} placeholder="e.g. Ashlee"
                            data-testid="care-details-caregiver-name" className={inputCls} maxLength={60} />
                    </label>
                    <label className="block text-xs font-semibold text-[#4C5551]">
                        Your loved one&apos;s name
                        <input value={lo} onChange={e => setLo(e.target.value)} placeholder="e.g. Mom, or Rosa"
                            data-testid="care-details-loved-one-name" className={inputCls} maxLength={60} />
                    </label>
                    <label className="block text-xs font-semibold text-[#4C5551]">
                        They are your…
                        <select value={rel} onChange={e => setRel(e.target.value)}
                            data-testid="care-details-relationship" className={inputCls}>
                            <option value="">Choose (optional)</option>
                            {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </label>
                    <button type="button" onClick={save} disabled={saving}
                        data-testid="care-details-save"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-teal text-white font-medium text-sm px-5 py-2.5 hover:bg-teal-dark transition-colors disabled:opacity-60">
                        {saving && <Loader2 size={14} className="animate-spin" />} Save
                    </button>
                    <div className="pt-2 border-t border-cream-dark space-y-1.5">
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-[#6E7A74]">Edit everything else</p>
                        <Link to="/intake" onClick={() => onOpenChange(false)} data-testid="care-details-full-intake-link"
                            className="flex items-center gap-2 text-sm text-teal-dark font-medium hover:underline underline-offset-4">
                            <ClipboardList size={14} strokeWidth={2} /> Update full intake — stage, concerns, living situation, location
                        </Link>
                        <Link to="/patient" onClick={() => onOpenChange(false)} data-testid="care-details-patient-profile-link"
                            className="flex items-center gap-2 text-sm text-teal-dark font-medium hover:underline underline-offset-4">
                            <HeartPulse size={14} strokeWidth={2} /> Patient profile — medications, insurance, contacts
                        </Link>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
